// Copyright ©️ 2026 eVolve MEP, LLC

import type { APSViewerStore } from 'components/Autodesk/extensions/APSViewerStoreExtension/createAPSViewerStore';
import type { DimensioningStore } from 'components/Autodesk/extensions/DimensioningExtension/createDimensioningStore';
import { DimensioningPanel } from 'components/Autodesk/extensions/DimensioningExtension/DimensioningPanel';
import type { ElementTagsStore } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createElementTagsStore';
import {
  createRenderedTagsStore,
  type RenderedTagsStore,
} from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createRenderedTagsStore';
import type { TagInteractionCallbacks } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/TagLabel';
import {
  createRenderedRackLabelsStore,
  type RenderedRackLabelsStore,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/createRenderedRackLabelsStore';
import { RackDimensionRenderingManager } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/RackDimensionRenderingManager';
import { getLineageUrn } from 'components/Autodesk/extensions/utils';
import type { MountPortalFn } from 'components/Autodesk/types';
import { isNil } from 'helpers/isNotNil';

import { TagRenderingManager } from './ElementTags/TagRenderingManager';

const { Autodesk } = window;

type Opts = {
  viewerStore: APSViewerStore;
  elementTagsStore: ElementTagsStore;
  dimensioningStore: DimensioningStore;
  mountPortal: MountPortalFn;
};

/**
 * Extension that provides a panel showing element tags and automatic conduit-rack dimensions.
 *
 * UI is mounted via `mountPortal` — a callback supplied by `ForgeViewerFromModel` — which injects
 * the React tree into the existing app tree via `ReactDOM.createPortal`. This gives full access
 * to `MantineProvider`, `EvolveApiProvider`, and all other context providers without creating a
 * separate `ReactDOM.Root`.
 */
export class DimensioningExtension extends Autodesk.Viewing.Extension {
  /** Store for accessing the APS viewer state. Prefer over direct use of `this.viewer` when possible. */
  private readonly viewerStore: APSViewerStore;
  /** Store for managing the tags related to this model */
  private readonly elementTagsStore: ElementTagsStore;
  /** Store for the dimensioning UI state, and for the elements React points the measurement at. */
  private readonly dimensioningStore: DimensioningStore;

  /** Small store to bridge the computed display state of tags from `TagRenderingManager` to React. */
  private readonly renderedTagsStore: RenderedTagsStore;
  /** Helper class to perform rendering tasks for tags on the model. */
  private readonly tagRenderingManager: TagRenderingManager;

  /** Small store to bridge projected dimension label positions to React. */
  private readonly renderedRackLabelsStore: RenderedRackLabelsStore;
  /** Helper class that measures conduit racks and draws their dimensions on the model. */
  private readonly rackDimensionRenderingManager: RackDimensionRenderingManager;

  private readonly mountPortal: MountPortalFn;
  private cleanupPortal: (() => void) | undefined;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, opts: Opts) {
    super(viewer, opts);
    this.viewerStore = opts.viewerStore;
    this.elementTagsStore = opts.elementTagsStore;
    this.dimensioningStore = opts.dimensioningStore;
    this.mountPortal = opts.mountPortal;

    this.renderedTagsStore = createRenderedTagsStore();

    this.tagRenderingManager = new TagRenderingManager(
      this.viewer,
      this.viewerStore,
      this.elementTagsStore,
      this.renderedTagsStore,
    );

    this.renderedRackLabelsStore = createRenderedRackLabelsStore();

    this.rackDimensionRenderingManager = new RackDimensionRenderingManager(
      this.viewer,
      this.dimensioningStore,
      this.renderedRackLabelsStore,
    );
  }

  load() {
    // All callbacks are created once here so React never sees reference changes across re-renders.
    const tagCallbacks: TagInteractionCallbacks = {
      onDragStart: (id) => this.tagRenderingManager.onLabelDragStart(id),
      onDragCommit: (id, cx, cy, grabX, grabY) => this.tagRenderingManager.onLabelDragCommit(id, cx, cy, grabX, grabY),
      onDragCancel: (id) => this.tagRenderingManager.onLabelDragCancel(id),
      onTagClick: (externalId) => {
        const dbId = this.viewerStore.getSnapshot().externalIdLookup?.[externalId];
        if (isNil(dbId)) return;
        this.viewer.select([dbId]);
      },
      getContainerRect: () => this.viewer.container.getBoundingClientRect(),
    };

    this.cleanupPortal = this.mountPortal(
      this.viewer.container,
      <DimensioningPanel
        renderedTagsStore={this.renderedTagsStore}
        renderedRackLabelsStore={this.renderedRackLabelsStore}
        tagCallbacks={tagCallbacks}
        getUrn={() => getLineageUrn(this.viewer.model)}
      />,
    );
    return true;
  }

  unload() {
    this.renderedTagsStore.clear();
    this.elementTagsStore.clear();
    this.renderedRackLabelsStore.clear();
    this.dimensioningStore.clear();
    // The viewer starts tearing down internals right after `unload` returns true, so tear down UI + viewer resources synchronously.
    this.tagRenderingManager.dispose();
    this.rackDimensionRenderingManager.dispose();
    this.cleanupPortal?.();
    this.cleanupPortal = undefined;
    return true;
  }
}
